import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useDeviceClass } from "@/hooks/use-device";

export const Route = createFileRoute("/debug-device")({
  head: () => ({
    meta: [
      { title: "Debug Device — Rawan UI" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: DebugDevicePage,
});

const CACHE_KEY = "rawanui:deviceClass";

type Signals = {
  userAgent: string;
  platform: string;
  hardwareConcurrency: number;
  deviceMemory: number | "unknown";
  maxTouchPoints: number;
  gpuVendor: string;
  gpuRenderer: string;
  pointerFine: boolean;
  hoverHover: boolean;
  viewport: string;
  devicePixelRatio: number;
  cachedVerdict: string;
};

function readGpu(): { vendor: string; renderer: string } {
  try {
    const canvas = document.createElement("canvas");
    const gl = (canvas.getContext("webgl") ||
      canvas.getContext("experimental-webgl")) as WebGLRenderingContext | null;
    if (!gl) return { vendor: "(no webgl)", renderer: "(no webgl)" };
    const ext = gl.getExtension("WEBGL_debug_renderer_info");
    const vendor = ext
      ? String(gl.getParameter(ext.UNMASKED_VENDOR_WEBGL) || "")
      : String(gl.getParameter(gl.VENDOR) || "");
    const renderer = ext
      ? String(gl.getParameter(ext.UNMASKED_RENDERER_WEBGL) || "")
      : String(gl.getParameter(gl.RENDERER) || "");
    return { vendor, renderer };
  } catch (e) {
    return { vendor: "(err)", renderer: String(e) };
  }
}

function readSignals(): Signals {
  const nav = navigator as Navigator & { deviceMemory?: number; platform?: string };
  const { vendor, renderer } = readGpu();
  let cached = "(none)";
  try {
    cached = sessionStorage.getItem(CACHE_KEY) || "(none)";
  } catch {
    /* ignore */
  }
  return {
    userAgent: nav.userAgent || "",
    platform: nav.platform || "",
    hardwareConcurrency: nav.hardwareConcurrency || 0,
    deviceMemory: nav.deviceMemory ?? "unknown",
    maxTouchPoints: nav.maxTouchPoints || 0,
    gpuVendor: vendor,
    gpuRenderer: renderer,
    pointerFine: window.matchMedia?.("(pointer: fine)").matches ?? false,
    hoverHover: window.matchMedia?.("(hover: hover)").matches ?? false,
    viewport: `${window.innerWidth}×${window.innerHeight}`,
    devicePixelRatio: window.devicePixelRatio || 1,
    cachedVerdict: cached,
  };
}

function DebugDevicePage() {
  const device = useDeviceClass();
  const [sig, setSig] = useState<Signals | null>(null);

  useEffect(() => {
    setSig(readSignals());
  }, []);

  const verdict = !device.ready
    ? "… loading"
    : device.isDesktop
      ? "DESKTOP 🖥️"
      : device.isTablet
        ? "TABLET 📱"
        : "MOBILE 📱";

  const clearCache = () => {
    try {
      sessionStorage.removeItem(CACHE_KEY);
    } catch {
      /* ignore */
    }
    window.location.reload();
  };

  const copyAll = async () => {
    if (!sig) return;
    const payload = JSON.stringify({ verdict, device, signals: sig }, null, 2);
    try {
      await navigator.clipboard.writeText(payload);
      alert("تم النسخ ✓");
    } catch {
      alert(payload);
    }
  };

  return (
    <div dir="ltr" className="min-h-dvh bg-background text-foreground p-6">
      <div className="mx-auto max-w-2xl space-y-6">
        <header dir="rtl" className="space-y-1">
          <h1 className="text-2xl font-bold">وضع اختبار الجهاز</h1>
          <p className="text-sm text-muted-foreground">
            صفحة تشخيص لتأكيد قرار <code>handheld</code> /{" "}
            <code>desktop</code> وعرض إشارات الجهاز الخام.
          </p>
        </header>

        <section className="rounded-xl border bg-card p-5">
          <div className="text-xs uppercase tracking-wider text-muted-foreground">
            Verdict
          </div>
          <div className="mt-1 text-3xl font-bold">{verdict}</div>
          <div className="mt-2 text-xs text-muted-foreground">
            isDesktop: {String(device.isDesktop)} · isMobile:{" "}
            {String(device.isMobile)} · isTablet: {String(device.isTablet)}
          </div>
        </section>

        <section className="rounded-xl border bg-card p-5">
          <div className="mb-3 text-xs uppercase tracking-wider text-muted-foreground">
            Hardware signals
          </div>
          {sig ? (
            <dl className="grid grid-cols-1 gap-y-2 text-sm font-mono">
              <Row k="GPU renderer" v={sig.gpuRenderer || "(empty)"} />
              <Row k="GPU vendor" v={sig.gpuVendor || "(empty)"} />
              <Row k="hardwareConcurrency" v={String(sig.hardwareConcurrency)} />
              <Row k="deviceMemory (GB)" v={String(sig.deviceMemory)} />
              <Row k="maxTouchPoints" v={String(sig.maxTouchPoints)} />
              <Row k="pointer: fine" v={String(sig.pointerFine)} />
              <Row k="hover: hover" v={String(sig.hoverHover)} />
              <Row k="platform" v={sig.platform} />
              <Row k="viewport" v={sig.viewport} />
              <Row k="devicePixelRatio" v={String(sig.devicePixelRatio)} />
              <Row k="sessionStorage cache" v={sig.cachedVerdict} />
              <Row k="userAgent" v={sig.userAgent} wrap />
            </dl>
          ) : (
            <div className="text-sm text-muted-foreground">… loading</div>
          )}
        </section>

        <div className="flex flex-wrap gap-3">
          <button
            onClick={copyAll}
            className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:opacity-90"
          >
            نسخ كل الإشارات
          </button>
          <button
            onClick={clearCache}
            className="rounded-md border px-4 py-2 text-sm font-medium hover:bg-accent"
          >
            مسح الكاش وإعادة الفحص
          </button>
          <button
            onClick={() => setSig(readSignals())}
            className="rounded-md border px-4 py-2 text-sm font-medium hover:bg-accent"
          >
            تحديث القراءات
          </button>
        </div>

        <p dir="rtl" className="text-xs text-muted-foreground">
          ملاحظة: القرار يُخزَّن في <code>sessionStorage</code> تحت المفتاح{" "}
          <code>{CACHE_KEY}</code>. استخدم زر "مسح الكاش" لإعادة الاكتشاف من
          الصفر بعد تبديل DevTools أو "Request desktop site".
        </p>
      </div>
    </div>
  );
}

function Row({ k, v, wrap }: { k: string; v: string; wrap?: boolean }) {
  return (
    <div className="flex gap-3 border-b border-border/40 pb-1.5 last:border-0">
      <dt className="w-44 shrink-0 text-muted-foreground">{k}</dt>
      <dd className={wrap ? "break-all" : "truncate"}>{v}</dd>
    </div>
  );
}
